export interface CrossTabMessage<T = unknown> {
  type: string;
  sourceId: string;
  sentAt: string;
  payload?: T;
}

/** Shared by ordinary mutations and destructive restore critical sections. */
export const USER_DATA_WRITE_LOCK = "user-data-write";

export class CrossTabCoordinator {
  private readonly sourceId = createId();
  private readonly channel?: BroadcastChannel;
  private readonly listeners = new Set<(message: CrossTabMessage) => void>();
  private localLock = Promise.resolve();

  constructor(channelName = "diet-tracker") {
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(channelName);
      this.channel.addEventListener("message", (event: MessageEvent<unknown>) => {
        const message = event.data;
        if (isMessage(message) && message.sourceId !== this.sourceId) {
          this.listeners.forEach((listener) => listener(message));
        }
      });
    }
  }

  publish<T>(type: string, payload?: T): void {
    this.channel?.postMessage({ type, payload, sourceId: this.sourceId, sentAt: new Date().toISOString() });
  }

  subscribe(listener: (message: CrossTabMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async exclusive<T>(name: string, work: () => Promise<T>): Promise<T> {
    if (navigator.locks?.request) {
      return navigator.locks.request(`diet-tracker:${name}`, { mode: "exclusive" }, work);
    }
    // Older browsers get an in-tab mutex. Supported mobile Chrome uses Web Locks,
    // which is the required cross-tab path for restore and schema coordination.
    const previous = this.localLock;
    let release!: () => void;
    this.localLock = new Promise<void>((resolve) => { release = resolve; });
    await previous;
    try {
      return await work();
    } finally {
      release();
    }
  }

  close(): void {
    this.channel?.close();
    this.listeners.clear();
  }
}

function isMessage(input: unknown): input is CrossTabMessage {
  if (!input || typeof input !== "object") return false;
  const value = input as Partial<CrossTabMessage>;
  return typeof value.type === "string" && typeof value.sourceId === "string" && typeof value.sentAt === "string";
}

function createId(): string {
  return globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
}
